import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  Divider,
  Link,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
} from '@mui/material';

interface AccountCreationStepProps {
  data: any;
  onChange: (data: any) => void;
  isLoggedIn: boolean;
}

const AccountCreationStep: React.FC<AccountCreationStepProps> = ({
  data,
  onChange,
  isLoggedIn
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    onChange({
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Email validation function
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  // Simple password validation
  const validatePassword = (password: string): boolean => {
    return password.length >= 6;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Account Information
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Please provide your account details to get started with your venue account.
      </Typography>
      
      <Grid container spacing={3}>
        {!isLoggedIn && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Email Address"
                name="email"
                value={data.email}
                onChange={handleChange}
                type="email"
                autoComplete="email"
                error={data.email !== '' && !validateEmail(data.email)}
                helperText={data.email !== '' && !validateEmail(data.email) ? 'Please enter a valid email address' : ''}
                InputProps={{
                  onBlur: (e) => {
                    const input = e.target as HTMLInputElement;
                    if (input.value) {
                      onChange({ email: input.value.trim().toLowerCase() });
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Password"
                name="password"
                value={data.password}
                onChange={handleChange}
                type="password"
                autoComplete="new-password"
                error={data.password !== '' && !validatePassword(data.password)}
                helperText={data.password !== '' && !validatePassword(data.password) ? 'Password must be at least 6 characters' : ''}
              />
            </Grid>
          </>
        )}
        
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="Full Name"
            name="name"
            value={data.name}
            onChange={handleChange}
            autoComplete="name"
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Phone Number"
            name="phone"
            value={data.phone}
            onChange={handleChange}
            autoComplete="tel"
          />
        </Grid>
        
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <FormControl component="fieldset">
            <FormLabel component="legend">Account Type</FormLabel>
            <RadioGroup
              row
              name="accountType"
              value={data.accountType}
              onChange={handleChange}
            >
              <FormControlLabel
                value="business"
                control={<Radio />}
                label="Business Account"
              />
              <FormControlLabel
                value="individual"
                control={<Radio />}
                label="Individual Account"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {data.accountType === 'business' 
              ? 'Business accounts are for restaurants, event spaces, and other commercial venues.' 
              : 'Individual accounts are for private venue owners, such as those renting out private properties.'}
          </Typography>
        </Grid>
        
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="acceptTerms"
                checked={data.acceptTerms}
                onChange={handleChange}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                I accept the <Link href="/terms" target="_blank">Terms of Service</Link> and <Link href="/privacy" target="_blank">Privacy Policy</Link>.
              </Typography>
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AccountCreationStep;
